import React, { useState, useEffect } from "react";
import { TableToolbar } from "./TableToolbar";
import { ReusableTable } from "./ReusableTable";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

export type ColumnConfig = {
  key: string;
  header: string;
  accessorKey?: string;
  type?: "text" | "number" | "date" | "boolean" | "image" | "select";
  options?: string[];
  hidden?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
  width?: number | string;
  sortable?: boolean;
  filterable?: boolean;
};

export type RowAction = {
  label: string;
  icon?: React.ComponentType<any>;
  onClick: (row: any) => void;
  className?: string;
  showCondition?: (row: any) => boolean;
};

export type TableConfig = {
  tableName: string;
  pageTitle: string;
  columns: ColumnConfig[];
  rowActions: RowAction[];
  defaultSortColumn?: string;
  defaultSortDirection?: "asc" | "desc";
  searchFields?: string[];
  addButtonText?: string;
  onRowClick?: (row: any) => void;
  primaryKeyField?: string;
  modal?: {
    component: React.ComponentType<any>;
    props?: Record<string, any>;
  };
};

type ReusableTablePageProps = {
  config: TableConfig;
  fetchData: () => Promise<any[]>;
  onAddNew?: () => void;
  onImport?: (data: any[]) => Promise<void>;
  onExport?: () => Promise<void>;
  onExportTemplate?: () => Promise<void>;
  additionalFilters?: Record<string, any>;
  isLoading?: boolean;
  isExporting?: boolean;
  error?: string | null;
};

export const ReusableTablePage: React.FC<ReusableTablePageProps> = ({
  config,
  fetchData,
  onAddNew,
  onImport,
  onExport,
  onExportTemplate,
  additionalFilters = {},
  isLoading: externalLoading = false,
  error: externalError
}) => {
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<{ key: string; direction: "asc" | "desc" | null }>({
    key: config.defaultSortColumn || config.columns[0].key,
    direction: config.defaultSortDirection || null,
  });
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    config.columns.filter(col => !col.hidden).map(col => col.key)
  );
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const { toast } = useToast();
  const [realtimeInitialized, setRealtimeInitialized] = useState(false);

  const loadData = async (showToast: boolean = true) => {
    try {
      console.log(`[ReusableTablePage] Loading data for ${config.tableName}...`);
      const data = await fetchData();
      console.log(`[ReusableTablePage] Loaded ${data.length} items for ${config.tableName}`);
      setData(data);
      applyFiltersAndSort(data);
      if (showToast) {
        toast({
          title: `Data refreshed`,
          description: `Successfully loaded ${data.length} items`,
        });
      }
    } catch (err) {
      console.error(`[ReusableTablePage] Error loading data for ${config.tableName}:`, err);
      const errorMessage = err instanceof Error ? err.message : `Error loading ${config.tableName}`;
      toast({
        title: `Error loading ${config.tableName}`,
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const applyFiltersAndSort = (dataToProcess: any[] = data) => {
    console.log(`[ReusableTablePage] Applying filters to ${config.tableName}`);
    try {
      let filtered = dataToProcess;
      
      if (searchQuery) {
        const searchFields = config.searchFields || config.columns.map(col => col.key);
        filtered = filtered.filter(row => {
          return searchFields.some(field => {
            const value = row[field];
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(searchQuery.toLowerCase());
          });
        });
      }

      // Apply column filters
      Object.entries(columnFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          filtered = filtered.filter(row => {
            const rowValue = row[key];
            if (rowValue === null || rowValue === undefined) return false;
            
            if (typeof value === "string") {
              return String(rowValue).toLowerCase().includes(value.toLowerCase());
            } else {
              return rowValue === value;
            }
          });
        }
      });
      
      // Apply additional filters
      Object.entries(additionalFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          filtered = filtered.filter(row => {
            const rowValue = row[key];
            if (rowValue === null || rowValue === undefined) return false;
            
            if (typeof value === "string") {
              return String(rowValue).toLowerCase().includes(value.toLowerCase());
            } else {
              return rowValue === value;
            }
          });
        }
      });

      // Apply sorting
      if (sortBy.key && sortBy.direction) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = a[sortBy.key];
          const bValue = b[sortBy.key];
          
          if (aValue === undefined || aValue === null) return sortBy.direction === "asc" ? -1 : 1;
          if (bValue === undefined || bValue === null) return sortBy.direction === "asc" ? 1 : -1;
          
          if (typeof aValue === "string" && typeof bValue === "string") {
            return sortBy.direction === "asc"
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          }
          
          return sortBy.direction === "asc" ? aValue - bValue : bValue - aValue;
        });
      }
      
      console.log(`[ReusableTablePage] Filtered data from ${dataToProcess.length} to ${filtered.length} rows`);
      setFilteredData(filtered);
    } catch (error) {
      console.error(`[ReusableTablePage] Error filtering data:`, error);
      setFilteredData(dataToProcess);
    }
  };

  const handleColumnVisibilityChange = (columns: string[]) => {
    console.log(`[ReusableTablePage] Column visibility changed:`, columns);
    setVisibleColumns(columns);
  };

  const handleSortChange = (key: string) => {
    console.log(`[ReusableTablePage] Sort changed to ${key}`);
    setSortBy(prev => {
      let newDirection: "asc" | "desc" | null = "asc";
      
      if (prev.key === key) {
        if (prev.direction === "asc") newDirection = "desc";
        else if (prev.direction === "desc") newDirection = null;
        else newDirection = "asc";
      }
      
      return {
        key,
        direction: newDirection
      };
    });
  };

  const handleColumnFilterChange = (key: string, value: any) => {
    console.log(`[ReusableTablePage] Column filter changed for ${key}:`, value);
    setColumnFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleImport = async (rows: any[]) => {
    console.log(`[ReusableTablePage] Importing ${rows.length} rows to ${config.tableName}`);
    try {
      if (onImport) {
        await onImport(rows);
        toast({ 
          title: "Import successful", 
          description: `Imported ${rows.length} rows to ${config.tableName}` 
        });
        loadData(false); // Don't show toast on refresh after import
      }
    } catch (error) {
      console.error(`[ReusableTablePage] Import error:`, error);
      toast({ 
        title: "Import failed", 
        description: String(error), 
        variant: "destructive" 
      });
    }
  };

  const handleExport = async () => {
    console.log(`[ReusableTablePage] Exporting ${filteredData.length} rows from ${config.tableName}`);
    try {
      if (onExport) {
        await onExport();
        toast({ title: `${config.tableName} exported successfully` });
      }
    } catch (error) {
      console.error(`[ReusableTablePage] Export error:`, error);
      toast({ 
        title: "Export failed", 
        description: String(error), 
        variant: "destructive" 
      });
    }
  };

  const handleExportTemplate = async () => {
    console.log(`[ReusableTablePage] Exporting template for ${config.tableName}`);
    try {
      if (onExportTemplate) {
        await onExportTemplate();
        toast({ title: `${config.tableName} template exported` });
      }
    } catch (error) {
      console.error(`[ReusableTablePage] Template export error:`, error);
      toast({ 
        title: "Template export failed", 
        description: String(error), 
        variant: "destructive" 
      });
    }
  };

  // Initial load
  useEffect(() => {
    loadData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters and sorting when relevant state changes
  useEffect(() => {
    applyFiltersAndSort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, columnFilters, sortBy, additionalFilters]);

  // Set up realtime updates for the table
  useEffect(() => {
    if (config.primaryKeyField && !realtimeInitialized) {
      setRealtimeInitialized(true);
      console.log(`[ReusableTablePage] Setting up realtime updates for ${config.tableName}`);
      const channel = supabase
        .channel(`${config.tableName}-changes`)
        .on('postgres_changes', 
          { event: '*', schema: 'public', table: config.tableName }, 
          (payload) => {
            console.log(`[ReusableTablePage] Received update for ${config.tableName}:`, payload);
            loadData(false); // Refresh data but don't show toast
          }
        )
        .subscribe();

      return () => {
        console.log(`[ReusableTablePage] Cleaning up realtime subscription for ${config.tableName}`);
        supabase.removeChannel(channel);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.tableName, config.primaryKeyField]);

  const getVisibleColumnConfigs = () => {
    return config.columns.filter(col => visibleColumns.includes(col.key));
  };

  return (
    <div className="container mx-auto py-8">
      <h1 className="text-2xl font-bold mb-6">{config.pageTitle}</h1>
      
      <TableToolbar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        columns={config.columns}
        visibleColumns={visibleColumns}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        onRefresh={() => loadData(true)}
        onImport={onImport ? handleImport : undefined}
        onExport={onExport ? handleExport : undefined}
        onExportTemplate={onExportTemplate ? handleExportTemplate : undefined}
        onAddNew={onAddNew}
        addButtonText={config.addButtonText || "Add New"}
      />
      
      <div className="mt-4 relative">
        {externalLoading && (
          <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-2 bg-background/90 p-4 rounded-md shadow">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p>Loading data...</p>
            </div>
          </div>
        )}
        <ReusableTable
          data={filteredData}
          columns={getVisibleColumnConfigs()}
          rowActions={config.rowActions}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          onRowClick={config.onRowClick}
          onColumnFilterChange={handleColumnFilterChange}
          columnFilters={columnFilters}
          isLoading={externalLoading}
        />
      </div>
    </div>
  );
};
